<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ start: '', end: '' })
  },
  highlightedDates: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'apply'])

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const currentDate = ref(new Date())
const selectedDates = ref({
  start: props.modelValue.start ? new Date(props.modelValue.start) : null,
  end: props.modelValue.end ? new Date(props.modelValue.end) : null
})

const currentMonth = computed(() => months[currentDate.value.getMonth()])
const currentYear = computed(() => currentDate.value.getFullYear())

const daysInMonth = computed(() => {
  const date = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth() + 1, 0)
  return date.getDate()
})

const firstDayOfMonth = computed(() => {
  const date = new Date(currentDate.value.getFullYear(), currentDate.value.getMonth(), 1)
  return date.getDay()
})

const calendarDays = computed(() => {
  const days = []
  const totalDays = daysInMonth.value
  const firstDay = firstDayOfMonth.value

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(null)
  }

  // Add the days of the month
  for (let i = 1; i <= totalDays; i++) {
    days.push(i)
  }

  return days
})

const isDateInRange = (date) => {
  if (!date || !selectedDates.value.start) return false
  
  const compareDate = date.getTime()
  const startDate = new Date(selectedDates.value.start).getTime()
  const endDate = selectedDates.value.end ? 
    new Date(selectedDates.value.end).getTime() : 
    (hoverDate.value ? hoverDate.value.getTime() : null)
  
  if (!endDate) return false

  // Check if date is between start and end (exclusive)
  return compareDate > Math.min(startDate, endDate) && 
         compareDate < Math.max(startDate, endDate)
}

const isDateSelected = (date) => {
  if (!date) return false
  
  // Create comparison date at noon
  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0)
  const startDate = selectedDates.value.start ? new Date(selectedDates.value.start) : null
  const endDate = selectedDates.value.end ? new Date(selectedDates.value.end) : null
  
  return (
    startDate?.getTime() === compareDate.getTime() ||
    endDate?.getTime() === compareDate.getTime()
  )
}

const handleDateClick = (day, year, month) => {
  if (!day) return
  
  // Create date at noon to avoid timezone issues
  const clickedDate = new Date(year, month, day, 12, 0, 0, 0)
  
  if (!selectedDates.value.start || (selectedDates.value.start && selectedDates.value.end)) {
    // First click or starting new selection
    selectedDates.value = {
      start: clickedDate,
      end: null
    }
  } else {
    // Second click - completing the range
    if (clickedDate < selectedDates.value.start) {
      selectedDates.value = {
        start: clickedDate,
        end: selectedDates.value.start
      }
    } else {
      selectedDates.value.end = clickedDate
    }
  }
}

const leftDate = ref(new Date())
const rightDate = ref(new Date())

// Initialize right date to next month
rightDate.value.setMonth(rightDate.value.getMonth() + 1)

// Separate computed properties for each calendar
const leftMonth = computed(() => leftDate.value.getMonth())
const leftYear = computed(() => leftDate.value.getFullYear())
const rightMonth = computed(() => rightDate.value.getMonth())
const rightYear = computed(() => rightDate.value.getFullYear())

// Update navigation functions
const prevLeftMonth = () => {
  const newDate = new Date(leftDate.value)
  newDate.setMonth(newDate.getMonth() - 1)
  if (newDate < rightDate.value) {
    leftDate.value = newDate
  }
}

const nextLeftMonth = () => {
  const newDate = new Date(leftDate.value)
  newDate.setMonth(newDate.getMonth() + 1)
  if (newDate < rightDate.value) {
    leftDate.value = newDate
  }
}

const prevRightMonth = () => {
  const newDate = new Date(rightDate.value)
  newDate.setMonth(newDate.getMonth() - 1)
  if (newDate > leftDate.value) {
    rightDate.value = newDate
  }
}

const nextRightMonth = () => {
  const newDate = new Date(rightDate.value)
  newDate.setMonth(newDate.getMonth() + 1)
  rightDate.value = newDate
}

const applyRange = () => {
  if (selectedDates.value.start && selectedDates.value.end) {
    const start = selectedDates.value.start
    const end = selectedDates.value.end
    
    emit('update:modelValue', {
      start: `${start.getFullYear()}-${(start.getMonth() + 1).toString().padStart(2, '0')}-${start.getDate().toString().padStart(2, '0')}`,
      end: `${end.getFullYear()}-${(end.getMonth() + 1).toString().padStart(2, '0')}-${end.getDate().toString().padStart(2, '0')}`
    })
    emit('apply')
  }
}

watch(() => props.modelValue, (newValue) => {
  selectedDates.value = {
    start: newValue.start ? new Date(newValue.start + 'T12:00:00') : null,
    end: newValue.end ? new Date(newValue.end + 'T12:00:00') : null
  }
}, { deep: true })

const getCalendarDays = (year, month) => {
  const days = []
  let firstDay = new Date(year, month, 1).getDay()
  // Convert Sunday (0) to 6 for Estonian calendar (Monday = 0, Sunday = 6)
  firstDay = firstDay === 0 ? 6 : firstDay - 1
  
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(null)
  }

  // Add the days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i)
  }

  return days
}

const highlightedSet = computed(() => new Set(props.highlightedDates))

const isDateHighlighted = (day, year, month) => {
  if (!day) return false
  const m = (month + 1).toString().padStart(2, '0')
  const d = day.toString().padStart(2, '0')
  return highlightedSet.value.has(`${year}-${m}-${d}`)
}

const hoverDate = ref(null)

const handleDayHover = (day, year, month) => {
  if (!day) return
  if (selectedDates.value.start && !selectedDates.value.end) {
    hoverDate.value = new Date(year, month, day, 12, 0, 0, 0)
  }
}

const getDayClasses = (day, year, month) => {
  if (!day) return 'text-gray-400'
  
  const date = new Date(year, month, day, 12, 0, 0, 0)
  const dateTime = date.getTime()
  
  const isStartDate = selectedDates.value.start && 
    new Date(selectedDates.value.start).getTime() === dateTime
  const isEndDate = selectedDates.value.end && 
    new Date(selectedDates.value.end).getTime() === dateTime
  
  const inRange = isDateInRange(date)
  const isHovered = selectedDates.value.start && !selectedDates.value.end && 
    date >= selectedDates.value.start && date <= hoverDate.value

  return {
    'hover:bg-gray-100': !isStartDate && !isEndDate && !inRange,
    'bg-pink-500 text-white font-bold': isStartDate || isEndDate,
    'bg-pink-50': (inRange || isHovered) && !isStartDate && !isEndDate,
    'cursor-pointer': true,
    'rounded-l-full border-2 border-pink-600': isStartDate,
    'rounded-r-full border-2 border-pink-600': isEndDate,
    'scale-110 z-10': isStartDate || isEndDate,
    'shadow-md': isStartDate || isEndDate,
  }
}

const formatDate = (date) => {
  return date.toLocaleDateString('et-EE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

const getFormattedDate = (date) => {
  if (!date) return 'Not selected'
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

onMounted(() => {
  if (props.modelValue.start) {
    selectedDates.value.start = new Date(props.modelValue.start + 'T12:00:00')
  }
  if (props.modelValue.end) {
    selectedDates.value.end = new Date(props.modelValue.end + 'T12:00:00')
  }
})
</script>

<template>
  <div class="bg-white">
    <div class="flex gap-6">
      <!-- Left Calendar -->
      <div class="flex-1">
        <div class="flex justify-between items-center mb-4">
          <button 
            @click="prevLeftMonth"
            class="p-1 hover:bg-gray-100 rounded"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="font-medium">
            {{ months[leftMonth] }} {{ leftYear }}
          </div>
          <button 
            @click="nextLeftMonth"
            class="p-1 hover:bg-gray-100 rounded"
            :disabled="leftMonth === rightMonth - 1 && leftYear === rightYear"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>

        <!-- Week Days -->
        <div class="grid grid-cols-7 mb-2">
          <div 
            v-for="day in weekDays" 
            :key="day"
            class="text-center text-xs font-medium text-gray-500 py-2"
          >
            {{ day }}
          </div>
        </div>

        <!-- Calendar Days -->
        <div class="grid grid-cols-7 gap-1">
          <div
            v-for="(day, index) in getCalendarDays(leftYear, leftMonth)"
            :key="index"
            @click="handleDateClick(day, leftYear, leftMonth)"
            @mouseover="handleDayHover(day, leftYear, leftMonth)"
            @mouseleave="hoverDate = null"
            class="aspect-square flex flex-col items-center justify-center text-sm transition-all duration-150 relative"
            :class="getDayClasses(day, leftYear, leftMonth)"
          >
            <span>{{ day }}</span>
            <span
              v-if="isDateHighlighted(day, leftYear, leftMonth)"
              class="fixture-dot"
            ></span>
          </div>
        </div>
      </div>

      <!-- Right Calendar -->
      <div class="flex-1">
        <div class="flex justify-between items-center mb-4">
          <button 
            @click="prevRightMonth"
            class="p-1 hover:bg-gray-100 rounded"
            :disabled="rightMonth === leftMonth + 1 && rightYear === leftYear"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="font-medium">
            {{ months[rightMonth] }} {{ rightYear }}
          </div>
          <button 
            @click="nextRightMonth"
            class="p-1 hover:bg-gray-100 rounded"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>

        <!-- Week Days -->
        <div class="grid grid-cols-7 mb-2">
          <div 
            v-for="day in weekDays" 
            :key="day"
            class="text-center text-xs font-medium text-gray-500 py-2"
          >
            {{ day }}
          </div>
        </div>

        <!-- Calendar Days -->
        <div class="grid grid-cols-7 gap-1">
          <div
            v-for="(day, index) in getCalendarDays(rightYear, rightMonth)"
            :key="index"
            @click="handleDateClick(day, rightYear, rightMonth)"
            @mouseover="handleDayHover(day, rightYear, rightMonth)"
            @mouseleave="hoverDate = null"
            class="aspect-square flex flex-col items-center justify-center text-sm transition-all duration-150 relative"
            :class="getDayClasses(day, rightYear, rightMonth)"
          >
            <span>{{ day }}</span>
            <span
              v-if="isDateHighlighted(day, rightYear, rightMonth)"
              class="fixture-dot"
            ></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected Range Display -->
    <div class="mt-4 text-sm text-gray-600 flex justify-between border-t pt-4">
      <div>
        <div class="font-medium text-gray-700 mb-1">Start Date</div>
        <div>{{ getFormattedDate(selectedDates.start) }}</div>
      </div>
      <div class="text-right">
        <div class="font-medium text-gray-700 mb-1">End Date</div>
        <div>{{ getFormattedDate(selectedDates.end) }}</div>
      </div>
    </div>

    <!-- Apply Button -->
    <button
      @click="applyRange"
      :disabled="!selectedDates.start || !selectedDates.end"
      class="mt-4 w-full bg-pink-500 text-white py-2 rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Apply Range
    </button>
  </div>
</template>

<style scoped>
.bg-pink-50 {
  position: relative;
}

.bg-pink-50::before {
  content: '';
  position: absolute;
  inset: 0;
  background-color: rgb(253 242 248 / 1);
  z-index: -1;
}

/* Add styles for better visual feedback */
.scale-110 {
  transform: scale(1.1);
}

.z-10 {
  z-index: 10;
}

button:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.bg-white {
  width: 100%;
}

.fixture-dot {
  position: absolute;
  bottom: 2px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: #ec4899;
}
</style> 